import { xor } from 'lodash-es';
import { useCallback, useMemo, useRef } from 'react';
import {
    useQueryParam,
    withDefault,
    NumberParam,
    StringParam,
    QueryParamOptions,
    BooleanParam,
    JsonParam
} from 'use-query-params';
import { HBDropdownOption } from '../HBDropdown/HBDropdown.types';
import type {
    ColumnSortState,
    HBTableQueryStateHelpers,
    PaginationState,
    HBTableQueryState,
    HBTableFilters
} from './HBTable.types';

const queryParamOptions: QueryParamOptions = {
    updateType: 'replaceIn',
    includeAllParams: true,
    removeDefaultsFromUrl: true
};

export default function useTableQuery<T extends HBTableFilters>(
    queryParamPrefix: string,
    defaultState: HBTableQueryState<T>
): [HBTableQueryState<T>, HBTableQueryStateHelpers<T>] {
    const defaultStateRef = useRef(defaultState);

    const [pageIndex, setPageIndex] = useQueryParam(
        `${queryParamPrefix}_page_index`,
        withDefault(NumberParam, defaultStateRef.current.pagination.pageIndex),
        queryParamOptions
    );

    const [pageSize, setPageSize] = useQueryParam(
        `${queryParamPrefix}_page_size`,
        withDefault(NumberParam, defaultStateRef.current.pagination.pageSize),
        queryParamOptions
    );

    const [sortId, setSortId] = useQueryParam(
        `${queryParamPrefix}_sort_id`,
        withDefault(StringParam, defaultStateRef.current.sorting.id),
        queryParamOptions
    );

    const [sortDesc, setSortDesc] = useQueryParam(
        `${queryParamPrefix}_sort_desc`,
        withDefault(BooleanParam, defaultStateRef.current.sorting.desc),
        queryParamOptions
    );

    const [search, setSearch] = useQueryParam(
        `${queryParamPrefix}_q`,
        withDefault(StringParam, defaultStateRef.current.search),
        queryParamOptions
    );

    const [filters, setFilters] = useQueryParam<T>(
        `${queryParamPrefix}_filters`,
        withDefault(JsonParam, defaultStateRef.current.filters),
        queryParamOptions
    );

    const onPaginationChange = useCallback(
        (updater: (prev: PaginationState) => PaginationState) => {
            const newParams = updater({ pageIndex, pageSize });
            setPageIndex(newParams.pageIndex);
            setPageSize(newParams.pageSize);
        },
        [pageIndex, pageSize, setPageIndex, setPageSize]
    );

    const onSortingChange = useCallback(
        (updater: (prev: ColumnSortState) => ColumnSortState) => {
            const newParams = updater({ id: sortId, desc: sortDesc });
            setSortId(newParams.id);
            setSortDesc(newParams.desc);
        },
        [sortId, sortDesc, setSortId, setSortDesc]
    );

    const setFilter = useCallback(
        (filterId: keyof T, value: string | string[] | number | number[]) => {
            setFilters(prevFilters => ({ ...prevFilters, [filterId]: value }));
        },
        [setFilters]
    );

    const toggleSingleFilter = useCallback(
        (filterId: keyof T, value: string | number) => {
            setFilters(prevFilters => ({
                ...prevFilters,
                [filterId]: prevFilters[filterId] === value ? undefined : value
            }));
        },
        [setFilters]
    );

    const toggleMultiFilter = useCallback(
        (filterId: keyof T, value: string | number) => {
            setFilters(prevFilters => ({
                ...prevFilters,
                [filterId]: xor(prevFilters[filterId] as any[], [value])
            }));
        },
        [setFilters]
    );

    const toggleFilter = useCallback(
        (filterId: keyof T, value: string | number) => {
            if (Array.isArray(defaultStateRef.current.filters[filterId])) {
                toggleMultiFilter(filterId, value);
            } else {
                toggleSingleFilter(filterId, value);
            }
        },
        [toggleMultiFilter, toggleSingleFilter]
    );

    const onMultiDropdownChange = useCallback(
        (filterId: keyof T, value: HBDropdownOption[]) => {
            setFilters(prevFilters => ({
                ...prevFilters,
                [filterId]: value.map(v => v.value) as string[]
            }));
        },
        [setFilters]
    );

    const clearAllFilters = useCallback(() => {
        setFilters(() =>
            Object.entries(defaultStateRef.current.filters).reduce(
                (acc, [key, value]: [keyof T, any]) => {
                    acc[key] = Array.isArray(value) ? ([] as any) : undefined;
                    return acc;
                },
                {} as T
            )
        );
    }, [setFilters]);

    const state: HBTableQueryState<T> = useMemo(
        (): HBTableQueryState<T> => ({
            pagination: { pageIndex, pageSize },
            sorting: { id: sortId, desc: sortDesc },
            search,
            filters
        }),
        [pageIndex, pageSize, sortId, sortDesc, search, filters]
    );

    const helpers: HBTableQueryStateHelpers<T> = useMemo(
        (): HBTableQueryStateHelpers<T> => ({
            onPaginationChange,
            onSortingChange,
            setSearch,
            toggleFilter,
            setFilter,
            onMultiDropdownChange,
            setFilters,
            clearAllFilters
        }),
        [
            onPaginationChange,
            onSortingChange,
            setSearch,
            toggleFilter,
            setFilter,
            onMultiDropdownChange,
            setFilters,
            clearAllFilters
        ]
    );

    return [state, helpers];
}
